import { Action, ActionType } from 'typesafe-actions';
import { Observable, of, OperatorFunction } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, map, switchMap, withLatestFrom, tap, ignoreElements } from 'rxjs/operators';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { toReduxError } from '@universal/errors';
import { SearchData } from '@appTypes/GlobalSearchTypes';
import { resultFields } from '@components/layout/InternalSearchMetaData';
import { trackViewSearchResults } from '@utils/Tracking/Search/trackSearchEvents';
import { Actions, actions } from '@redux/modules/internalSiteSearch';
import { TypedEpic } from './types';

export const fetchSearchResultsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_SEARCH_RESULTS_REQUEST),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getSearchResults.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.post<SearchData>(ApiType.elasticSearchAPI, {
          signal,
          url: ApiPaths[ApiType.elasticSearchAPI].search,
          data: {
            query: state$.value.internalSiteSearch.query,
            page: {
              current: 1,
              size: 20,
            },
            result_fields: resultFields,
          },
        }),
      ).pipe(
        map((response) => actions.getSearchResults.success(response.data)),
        catchError((err) => of(actions.getSearchResults.failure(toReduxError(err, 'Could not get search results')))),
      ),
    ),
  );

export const fetchMoreSearchResultsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MORE_SEARCH_RESULTS_REQUEST),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMoreSearchResults.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.post<SearchData>(ApiType.elasticSearchAPI, {
          signal,
          url: ApiPaths[ApiType.elasticSearchAPI].search,
          data: {
            query: state$.value.internalSiteSearch.query,
            page: {
              current: state$.value.internalSiteSearch.currentPage + 1,
              size: 20,
            },
            result_fields: resultFields,
          },
        }),
      ).pipe(
        map((response) => actions.getMoreSearchResults.success(response.data)),
        catchError((err) =>
          of(actions.getMoreSearchResults.failure(toReduxError(err, 'Could not get more search results'))),
        ),
      ),
    ),
  );

export const trackSearchResultsViewEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_SEARCH_RESULTS_SUCCESS),
    tap(() => {
      const { submittedQuery, totalResults, isFromPopularTerm } = state$.value.internalSiteSearch;

      trackViewSearchResults({
        searchTerm: submittedQuery,
        resultCount: totalResults,
        popularTermShortcut: isFromPopularTerm,
      });
    }),
    ignoreElements(),
  );

export const epics: TypedEpic[] = [fetchSearchResultsEpic, fetchMoreSearchResultsEpic, trackSearchResultsViewEpic];
